const PLAN_FIELDS = [
  'contractId',
  'productKey',
  'productOrder',
  'sourceColumnIndex',
  'insurerName',
  'productName',
  'contractDate',
  'paymentPeriod',
  'coveragePeriod',
  'monthlyPremium',
]

export function positiveCoverageAmount(value) {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? Math.round(numeric) : 0
}

export function coverageSimulationRowKey(row) {
  const code = String(row?.coverageCode || '').trim()
  if (code && !code.startsWith('CUSTOM_')) return `CODE:${code}`
  return `NAME:${String(row?.coverageGroup || '').trim()}|${String(row?.coverageName || '').trim()}`
}

export function coverageSimulationPlanKey(product, fallback = '') {
  const productKey = String(product?.productKey || '').trim()
  if (productKey) return productKey
  if (positiveCoverageAmount(product?.contractId)) return `CONTRACT_${positiveCoverageAmount(product.contractId)}`
  const sourceColumnIndex = positiveCoverageAmount(product?.sourceColumnIndex)
  const insurerName = String(product?.insurerName || '').trim()
  const productName = String(product?.productName || '').trim()
  if (sourceColumnIndex) return `COLUMN_${sourceColumnIndex}_${insurerName}_${productName}`
  if (insurerName || productName) return `PLAN_${insurerName}_${productName}`
  return fallback || `PLAN_${Date.now()}`
}

function normalizedPlan(product, fallbackKey, order) {
  const plan = {}
  PLAN_FIELDS.forEach((field) => {
    plan[field] = product?.[field] ?? (field === 'monthlyPremium' ? 0 : '')
  })
  plan.productKey = coverageSimulationPlanKey(product, fallbackKey)
  plan.productOrder = positiveCoverageAmount(plan.productOrder) || order + 1
  plan.monthlyPremium = positiveCoverageAmount(plan.monthlyPremium)
  plan.planMode = String(product?.planMode || '').trim().toUpperCase() === 'REPLACE' ? 'REPLACE' : 'ADD'
  return plan
}

function rowProductMap(row, rowIndex) {
  const map = new Map()
  ;(Array.isArray(row?.products) ? row.products : []).forEach((product, productIndex) => {
    const key = coverageSimulationPlanKey(product, `ROW_${rowIndex}_PLAN_${productIndex}`)
    map.set(key, { ...product, productKey: key })
  })
  return map
}

export function normalizeCoverageRemodelingSimulation(report) {
  const baseRows = Array.isArray(report?.rows) ? report.rows : []
  const remodelingRows = Array.isArray(report?.remodelingRows) ? report.remodelingRows : []
  const baseByKey = new Map(baseRows.map((row, index) => [coverageSimulationRowKey(row, index), row]))
  const remodelingByKey = new Map(remodelingRows.map((row, index) => [coverageSimulationRowKey(row, index), row]))
  const rowKeys = [...baseByKey.keys()]
  remodelingByKey.forEach((_, key) => {
    if (!baseByKey.has(key)) rowKeys.push(key)
  })

  const plansByKey = new Map()
  remodelingRows.forEach((row, rowIndex) => {
    ;(Array.isArray(row?.products) ? row.products : []).forEach((product, productIndex) => {
      const key = coverageSimulationPlanKey(product, `ROW_${rowIndex}_PLAN_${productIndex}`)
      if (!plansByKey.has(key)) plansByKey.set(key, normalizedPlan(product, key, plansByKey.size))
    })
  })
  const plans = Array.from(plansByKey.values())

  const rows = rowKeys.map((key, index) => {
    const base = baseByKey.get(key) || remodelingByKey.get(key) || {}
    const remodeled = remodelingByKey.get(key) || {}
    const currentProductMap = rowProductMap(base, index)
    const productMap = rowProductMap(remodeled, index)
    return {
      ...base,
      ...remodeled,
      _simulationKey: key,
      _initialAmount: positiveCoverageAmount(base.totalAmount),
      _beforeAmount: positiveCoverageAmount(base.totalAmount),
      currentProducts: Array.from(currentProductMap.values()).map((product) => ({ ...product, _initialAmount: positiveCoverageAmount(product?.amount) })),
      displayOrder: positiveCoverageAmount(remodeled.displayOrder ?? base.displayOrder) || (index + 1) * 10,
      visible: remodeled.visible !== false && base.visible !== false,
      products: plans.map((plan) => ({
        ...plan,
        ...(productMap.get(plan.productKey) || {}),
        productKey: plan.productKey,
        amount: positiveCoverageAmount(productMap.get(plan.productKey)?.amount),
      })),
    }
  }).sort((a, b) => Number(a.displayOrder || 0) - Number(b.displayOrder || 0))

  return { plans, rows }
}

export function createCoverageRemodelingPlan(index = 0, key = '') {
  const productKey = key || `REMODEL_${Date.now()}_${index + 1}`
  return normalizedPlan({
    productKey,
    productName: `추천 플랜 ${index + 1}`,
    productOrder: index + 1,
  }, productKey, index)
}

export function coverageRemodelingRowTotal(row) {
  const products = Array.isArray(row?.products) ? row.products : []
  const proposedAmount = products.reduce((sum, product) => sum + positiveCoverageAmount(product?.amount), 0)
  const replacesCurrentPortfolio = products.some((product) => String(product?.planMode || '').toUpperCase() === 'REPLACE')
  return replacesCurrentPortfolio ? proposedAmount : positiveCoverageAmount(row?._beforeAmount) + proposedAmount
}

export function coverageSimulationCurrentRowTotal(row) {
  return (Array.isArray(row?.currentProducts) ? row.currentProducts : [])
    .reduce((sum, product) => sum + positiveCoverageAmount(product?.amount), 0)
}

function coverageSimulationInitialAmount(row) {
  return positiveCoverageAmount(row?._initialAmount ?? row?._beforeAmount)
}

export function coverageRemodelingDifference(row) {
  return coverageRemodelingRowTotal(row) - coverageSimulationInitialAmount(row)
}

export function coverageRemodelingRate(row) {
  const before = coverageSimulationInitialAmount(row)
  const difference = coverageRemodelingDifference(row)
  if (!before) return difference > 0 ? null : 0
  return difference / before
}

export function buildCoverageRemodelingReport(report, plans, rows) {
  const safePlans = Array.isArray(plans) ? plans : []
  const remodelingRows = (Array.isArray(rows) ? rows : []).map((row) => {
    const productsByKey = rowProductMap(row, 0)
    const products = safePlans.map((plan, planIndex) => {
      const amountRow = productsByKey.get(plan.productKey) || {}
      return {
        ...normalizedPlan(plan, plan.productKey, planIndex),
        amount: positiveCoverageAmount(amountRow.amount),
        coverageNames: String(amountRow.coverageNames || ''),
        coverageDetailLines: String(amountRow.coverageDetailLines || ''),
        note: String(amountRow.note || ''),
      }
    })
    const {
      _simulationKey,
      _initialAmount,
      _beforeAmount,
      currentProducts,
      ...persistedRow
    } = row
    return {
      ...persistedRow,
      totalAmount: coverageRemodelingRowTotal(row),
      sourceCoverageCount: products.filter((product) => positiveCoverageAmount(product.amount) > 0).length,
      products,
    }
  })
  const simulationRowsByKey = new Map((Array.isArray(rows) ? rows : [])
    .map((row, index) => [row?._simulationKey || coverageSimulationRowKey(row, index), row]))
  const currentRows = (Array.isArray(report?.rows) ? report.rows : []).map((sourceRow, index) => {
    const simulationRow = simulationRowsByKey.get(coverageSimulationRowKey(sourceRow, index))
    if (!simulationRow) return sourceRow
    const currentProducts = Array.isArray(simulationRow.currentProducts)
      ? simulationRow.currentProducts.map(({ _initialAmount, ...product }) => ({ ...product, amount: positiveCoverageAmount(product?.amount) }))
      : (Array.isArray(sourceRow.products) ? sourceRow.products : [])
    return {
      ...sourceRow,
      totalAmount: positiveCoverageAmount(simulationRow._beforeAmount),
      sourceCoverageCount: currentProducts.filter((product) => positiveCoverageAmount(product?.amount) > 0).length,
      products: currentProducts,
    }
  })
  return {
    ...(report || {}),
    rows: currentRows,
    remodelingRows,
  }
}

export function copyCurrentCoverageToRemodeling(report) {
  const currentRows = Array.isArray(report?.rows) ? report.rows : []
  const plansByKey = new Map()
  currentRows.forEach((row, rowIndex) => {
    ;(Array.isArray(row?.products) ? row.products : []).forEach((product, productIndex) => {
      const key = coverageSimulationPlanKey(product, `CURRENT_${rowIndex}_${productIndex}`)
      if (!plansByKey.has(key)) plansByKey.set(key, normalizedPlan({ ...product, planMode: 'REPLACE' }, key, plansByKey.size))
    })
  })
  const plans = Array.from(plansByKey.values())
  const rows = currentRows.map((row, rowIndex) => {
    const currentProducts = rowProductMap(row, rowIndex)
    return {
      ...row,
      _simulationKey: coverageSimulationRowKey(row, rowIndex),
      _initialAmount: positiveCoverageAmount(row.totalAmount),
      _beforeAmount: positiveCoverageAmount(row.totalAmount),
      currentProducts: Array.from(currentProducts.values()).map((product) => ({ ...product, _initialAmount: positiveCoverageAmount(product?.amount) })),
      products: plans.map((plan) => ({
        ...plan,
        ...(currentProducts.get(plan.productKey) || {}),
        productKey: plan.productKey,
        amount: positiveCoverageAmount(currentProducts.get(plan.productKey)?.amount),
      })),
    }
  })
  return { plans, rows }
}

export function formatCoverageSimulationAmount(value) {
  const amount = positiveCoverageAmount(value)
  if (!amount) return '-'
  const billions = Math.floor(amount / 100000000)
  const tenThousands = Math.floor((amount % 100000000) / 10000)
  const remainder = amount % 10000
  const parts = []
  if (billions) parts.push(`${billions.toLocaleString('ko-KR')}억`)
  if (tenThousands) parts.push(`${tenThousands.toLocaleString('ko-KR')}만원`)
  if (!parts.length && remainder) parts.push(`${remainder.toLocaleString('ko-KR')}원`)
  return parts.join(' ')
}

